import { z } from 'zod';

// Obchodný vestník + dlžníci VšZP (Týž. 19+, enrichment zo Slovensko.Digital SQL
// dumpov) — viď docs/CURRENT.md pre presný zdroj a obmedzenia.
export const insolvencyEventSchema = z.object({
  id: z.string(),
  eventType: z.enum([
    'konkurz_restrukturalizacia',
    'konkurz_vyrovnanie',
    'likvidacia',
    'znizenie_imania',
  ]),
  eventDate: z.string().nullable(),
  description: z.string().nullable(),
  sourceTable: z.string(),
  // "Čo sa speňažuje" (2026-07-18) — voľnotextový obsah uznesenia/oznamu z
  // Obchodného vestníka (viď db/schema/insolvency.ts komentár), best-effort
  // spätne dopárovaný, nie vždy prítomný (najmä konkurz_vyrovnanie/likvidácia/
  // zníženie imania majú toto typicky null).
  heading: z.string().nullable(),
  announcementText: z.string().nullable(),
  decisionText: z.string().nullable(),
  adviceText: z.string().nullable(),
});
export type InsolvencyEvent = z.infer<typeof insolvencyEventSchema>;

export const vszpDebtSchema = z.object({
  id: z.string(),
  amount: z.string(),
  publishedOn: z.string(),
});
export type VszpDebt = z.infer<typeof vszpDebtSchema>;

// Dlžníci Sociálnej poisťovne (2026-07-17, opendata-bulk-sync) — plný snapshot
// zoznamu, znovunaplnený pri každom behu (viď schema/insolvency.ts), preto bez
// vlastného publishedOn dátumu (na rozdiel od VšZP) — len missingPeriods, ak sa
// dá odvodiť z "chýbajúce podklady za obdobie".
export const socpoistDebtSchema = z.object({
  id: z.string(),
  amount: z.string(),
  missingPeriods: z.string().nullable(),
});
export type SocpoistDebt = z.infer<typeof socpoistDebtSchema>;

// IS REPLIK (Ministerstvo spravodlivosti SR, 2026-07-17) — živý per-firma SOAP
// lookup (nie batch import ako polia vyššie), viď replik-client.service.ts.
// Štruktúrovanejšie než Obchodný vestník text (Spravca/Sud/StavKonania) —
// zobrazuje sa ako doplnkový zdroj, nie náhrada eventov vyššie.
export const replikProceedingSchema = z.object({
  id: z.string(),
  typ: z.string(),
  spisovaZnackaSudu: z.string().nullable(),
  datumZacatiaKonania: z.string().nullable(),
  datumZacatiaProcesu: z.string().nullable(),
  datumUkonceniaProcesu: z.string().nullable(),
  sudNazov: z.string().nullable(),
  spravca: z.string().nullable(),
  stavKonania: z.string().nullable(),
});
export type ReplikProceeding = z.infer<typeof replikProceedingSchema>;

// RPVS Open Data (2026-07-18, Týž. 21) — Vymaz (výmaz z registra, ex offo je
// red-flag)/Pokuta (sankcia)/kvalifikovaný podnet (konanie o preskúmaní zápisu).
// Batch-synced spolu s UBO (PersonsService.syncForCompany), nie živý lookup ako
// REPLIK vyššie.
export const rpvsEventSchema = z.object({
  id: z.string(),
  eventType: z.enum([
    'vymaz_dobrovolny',
    'vymaz_ex_offo',
    'pokuta',
    'kvalifikovany_podnet',
    'verejny_funkcionar',
    'kuv_neoverene',
  ]),
  eventDate: z.string().nullable(),
  description: z.string().nullable(),
});
export type RpvsEvent = z.infer<typeof rpvsEventSchema>;

export const insolvencyInfoSchema = z.object({
  events: z.array(insolvencyEventSchema),
  vszpDebts: z.array(vszpDebtSchema),
  socpoistDebts: z.array(socpoistDebtSchema),
  replikProceedings: z.array(replikProceedingSchema),
  rpvsEvents: z.array(rpvsEventSchema),
});
export type InsolvencyInfo = z.infer<typeof insolvencyInfoSchema>;

// Fáza 1 inzertnej/insolvenčnej sekcie (2026-07-15) — agregátny feed naprieč
// všetkými firmami, na rozdiel od insolvencyInfoSchema vyššie (per-firma lookup).
export const insolvencyFeedRowSchema = insolvencyEventSchema.extend({
  ico: z.string(),
  companyName: z.string().nullable(),
  city: z.string().nullable(),
});
export type InsolvencyFeedRow = z.infer<typeof insolvencyFeedRowSchema>;

export const insolvencyFeedResponseSchema = z.object({
  results: z.array(insolvencyFeedRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type InsolvencyFeedResponse = z.infer<typeof insolvencyFeedResponseSchema>;
